
import React, { useState, useEffect } from 'react';
import { useApp, Role } from './AppContext';
import { Button, BrandLogo } from './Components';
import { TermsAndServices, PrivacyPolicy, InformedConsent } from './Legal';

export const AuthPage: React.FC<{ initialType: 'login' | 'register', setPage: (p: string) => void }> = ({ initialType, setPage }) => {
    const { login, register, loginGuest, resetUserPassword, showToast } = useApp();
    const [type, setType] = useState(initialType);
    const [form, setForm] = useState({ email: '', password: '', name: '', dob: '', phone: '', gender: '', address: '', guardianName: '', guardianPhone: '' });
    const [role, setRole] = useState<Role>('patient');
    const [loading, setLoading] = useState(false);
    
    // Forgot Password State
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetFeedback, setResetFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        const emailToReset = resetEmail.trim();
        if (!emailToReset || !emailToReset.includes('@')) {
            setResetFeedback({ type: 'error', message: 'Please enter a valid email address.' });
            return;
        }

        setResetLoading(true);
        setResetFeedback(null);
        try {
            await resetUserPassword(emailToReset);
            setResetFeedback({ 
                type: 'success', 
                message: `A secure password reset link was sent to ${emailToReset}. Please check your inbox and spam folder.` 
            });
        } catch (err: any) {
            setResetFeedback({ 
                type: 'error', 
                message: err.message || 'Unable to send reset email. Please verify your address and try again.' 
            });
        } finally {
            setResetLoading(false);
        }
    };
    
    // Age Validation Logic
    const [isMinor, setIsMinor] = useState(false);
    useEffect(() => {
        if (form.dob) {
            const birth = new Date(form.dob);
            const today = new Date();
            let age = today.getFullYear() - birth.getFullYear();
            const m = today.getMonth() - birth.getMonth();
            if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
                age--;
            }
            setIsMinor(age < 18);
        } else {
            setIsMinor(false);
        }
    }, [form.dob]);

    // Legal Agreement State
    const [agreed, setAgreed] = useState(false);
    const [viewingLegal, setViewingLegal] = useState<'terms' | 'privacy' | 'consent' | null>(null);

    useEffect(() => {
        setType(initialType);
        setAgreed(false); // Reset agreement on type change
    }, [initialType]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (type === 'register') {
            if (!agreed) {
                showToast("Consent Required", "You must agree to the Terms of Service, Privacy Policy, and Informed Consent to create an account.", "error");
                return;
            }
            if (!form.dob || !form.phone || !form.gender || !form.address) {
                showToast("Required Fields Missing", "Please fill in all mandatory fields (DoB, Phone, Gender, Address).", "error");
                return;
            }
            if (isMinor && (!form.guardianName || !form.guardianPhone)) {
                showToast("Guardian Required", "As you are under 18, we need your parent or guardian's details to proceed safely.", "error");
                return;
            }
        }

        setLoading(true);
        try { 
            if(type === 'login') await login(form.email, form.password); 
            else await register(form.name, form.email, form.password, role, { 
                dob: form.dob, 
                phone: form.phone, 
                gender: form.gender, 
                address: form.address,
                guardianName: form.guardianName,
                guardianPhone: form.guardianPhone
            }); 
            setPage('dashboard'); 
        } catch (e: any) { 
            showToast("Authentication Failed", e.message || "An authentication error occurred.", "error"); 
        }
        setLoading(false);
    };

    // Helper to render the legal modal without leaving the page
    const LegalModal = () => {
        if (!viewingLegal) return null;
        return (
            <div className="fixed inset-0 z-[600] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300">
                <div className="bg-white w-full max-w-4xl max-h-[85vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col relative animate-in zoom-in-95 duration-300 my-auto border border-slate-100">
                    <div className="flex items-center justify-between p-6 border-b border-slate-100 bg-white z-10">
                        <h3 className="text-xl font-black text-slate-900 tracking-tight">
                            {viewingLegal === 'terms' ? 'Terms of Service' : viewingLegal === 'privacy' ? 'Privacy Policy' : 'Informed Consent'}
                        </h3>
                        <button 
                            onClick={() => setViewingLegal(null)} 
                            className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-900 transition-all"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin p-2 bg-slate-50/30">
                        {viewingLegal === 'terms' ? (
                            <TermsAndServices className="py-8" />
                        ) : viewingLegal === 'privacy' ? (
                            <PrivacyPolicy className="py-8" />
                        ) : (
                            <InformedConsent className="py-8" />
                        )}
                    </div>
                    <div className="p-6 border-t border-slate-100 bg-white flex justify-between items-center">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            {viewingLegal === 'terms' ? 'ROVITA Legal' : 'HIPAA Compliance'}
                        </p>
                        <Button onClick={() => setViewingLegal(null)}>Acknowledge & Close</Button>
                    </div>
                </div>
            </div>
        );
    };

    // Forgot Password Modal
    const ForgotPasswordModal = () => {
        if (!showForgotModal) return null;
        return (
            <div className="fixed inset-0 z-[600] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-300" role="dialog" aria-modal="true" aria-labelledby="forgot-password-heading">
                <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-6 sm:p-8 border border-slate-100 relative animate-in zoom-in-95 duration-200">
                    <div className="flex items-center justify-between mb-5">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-xl shadow-2xs">
                            <i className="fa-solid fa-key"></i>
                        </div>
                        <button
                            type="button"
                            onClick={() => { setShowForgotModal(false); setResetFeedback(null); }}
                            className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300"
                            aria-label="Close modal"
                        >
                            <i className="fa-solid fa-xmark"></i>
                        </button>
                    </div>

                    <h3 id="forgot-password-heading" className="text-2xl font-black text-slate-900 mb-1.5 tracking-tight">
                        Reset Password
                    </h3>
                    <p className="text-xs text-slate-500 font-medium mb-6 leading-relaxed">
                        Enter your registered account email. We will send you a secure link to create a new password.
                    </p>

                    {resetFeedback && (
                        <div 
                            className={`p-4 rounded-2xl text-xs font-bold mb-5 flex items-start gap-3 animate-in fade-in ${
                                resetFeedback.type === 'success' 
                                    ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' 
                                    : 'bg-rose-50 text-rose-800 border border-rose-200'
                            }`}
                            role="alert"
                        >
                            <i className={`fa-solid ${resetFeedback.type === 'success' ? 'fa-circle-check text-emerald-600' : 'fa-circle-exclamation text-rose-600'} text-base shrink-0 mt-0.5`}></i>
                            <span className="leading-relaxed">{resetFeedback.message}</span>
                        </div>
                    )}

                    <form onSubmit={handleForgotPassword} className="space-y-4">
                        <div>
                            <label htmlFor="reset-email-field" className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5 ml-1">
                                Account Email Address
                            </label>
                            <input
                                id="reset-email-field"
                                type="email"
                                required
                                autoFocus
                                placeholder="e.g. name@example.com"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold text-slate-800 text-sm transition-all"
                            />
                        </div>

                        <div className="flex gap-2 pt-2">
                            <button
                                type="button"
                                onClick={() => { setShowForgotModal(false); setResetFeedback(null); }}
                                className="flex-1 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all"
                            >
                                Cancel
                            </button>
                            <Button
                                type="submit"
                                disabled={resetLoading}
                                className="flex-1 py-3.5 text-xs font-black shadow-md"
                            >
                                {resetLoading ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <i className="fa-solid fa-spinner fa-spin"></i>
                                        Sending...
                                    </span>
                                ) : 'Send Reset Link'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen flex items-center justify-center p-4 md:p-6 bg-slate-50 relative overflow-x-hidden py-12">
            <LegalModal />
            <ForgotPasswordModal />
            
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] md:w-[800px] h-[500px] md:h-[800px] bg-indigo-200/20 rounded-full blur-[80px] md:blur-[120px] -z-10"></div>
            
            <div className="max-w-2xl w-full bg-white p-8 md:p-12 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl border border-white relative overflow-hidden my-8">
                <div className="absolute top-0 right-0 w-24 h-24 md:w-32 md:h-32 bg-indigo-50 rounded-bl-[4rem] -z-10"></div>
                
                <div className="text-center mb-8">
                    <BrandLogo className="justify-center mb-6" iconOnly />
                    <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-2 tracking-tight">
                        {type === 'login' ? 'Welcome Back' : 'Create Account'}
                    </h2>
                    <p className="text-slate-400 font-medium text-sm md:text-base">
                        {type === 'login' ? 'Log in to access your portal.' : 'Join our health network today.'}
                    </p>
                </div>

                <form onSubmit={onSubmit} className="space-y-4 md:space-y-5">
                    {type === 'register' && (
                        <div className="space-y-4">
                            <input 
                                className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                                placeholder="Full Name" 
                                value={form.name} 
                                onChange={e => setForm({...form, name: e.target.value})} 
                                required 
                            />
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Date of Birth</label>
                                    <input 
                                        type="date"
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                                        value={form.dob} 
                                        onChange={e => setForm({...form, dob: e.target.value})} 
                                        required 
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Gender</label>
                                    <select 
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all appearance-none" 
                                        value={form.gender} 
                                        onChange={e => setForm({...form, gender: e.target.value})} 
                                        required
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                <input 
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                                    placeholder="Phone (e.g. +44 123...)" 
                                    value={form.phone} 
                                    onChange={e => setForm({...form, phone: e.target.value})} 
                                    required 
                                />
                                <input 
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                                    placeholder="Full Home Address" 
                                    value={form.address} 
                                    onChange={e => setForm({...form, address: e.target.value})} 
                                    required 
                                />
                            </div>

                            {isMinor && (
                                <div className="p-6 bg-amber-50 rounded-3xl border border-amber-100 space-y-4">
                                    <div className="flex items-center gap-2 mb-2">
                                        <i className="fa-solid fa-shield-halved text-amber-500"></i>
                                        <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Parent / Guardian Identification Required</p>
                                    </div>
                                    <p className="text-xs text-amber-800/70 font-medium mb-4 italic">You are under 18. We need your parent or guardian's details to complete your registration safely.</p>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <input 
                                            className="w-full p-4 bg-white rounded-2xl border-2 border-transparent focus:border-amber-500 outline-none font-bold transition-all" 
                                            placeholder="Parent Full Name" 
                                            value={form.guardianName} 
                                            onChange={e => setForm({...form, guardianName: e.target.value})} 
                                            required={isMinor} 
                                        />
                                        <input 
                                            className="w-full p-4 bg-white rounded-2xl border-2 border-transparent focus:border-amber-500 outline-none font-bold transition-all" 
                                            placeholder="Parent Phone Number" 
                                            value={form.guardianPhone} 
                                            onChange={e => setForm({...form, guardianPhone: e.target.value})} 
                                            required={isMinor} 
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    
                    <input 
                        className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                        type="email" 
                        placeholder="Email Address" 
                        value={form.email} 
                        onChange={e => setForm({...form, email: e.target.value})} 
                        autoComplete="username"
                        required 
                    />
                    <input 
                        className="w-full p-4 bg-slate-50 rounded-2xl border-2 border-transparent focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all" 
                        type="password" 
                        placeholder="Password" 
                        value={form.password} 
                        onChange={e => setForm({...form, password: e.target.value})} 
                        autoComplete={type === 'login' ? 'current-password' : 'new-password'}
                        required 
                    />

                    {type === 'login' && (
                        <div className="flex justify-end pr-1 -mt-1 mb-1">
                            <button
                                type="button"
                                onClick={() => {
                                    setResetEmail(form.email || '');
                                    setResetFeedback(null);
                                    setShowForgotModal(true);
                                }}
                                className="text-xs font-bold text-indigo-600 hover:text-indigo-800 hover:underline transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 rounded-md px-1 py-0.5"
                            >
                                Forgot password?
                            </button>
                        </div>
                    )}

                    {type === 'register' && (
                        <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-2xl">
                            {['patient', 'doctor'].map(r => (
                                <button 
                                    key={r} 
                                    type="button" 
                                    onClick={() => setRole(r as Role)} 
                                    className={`py-3 md:py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all ${role === r ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-indigo-400'}`}
                                >
                                    I am a {r}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Agreement Checkbox for Registration */}
                    {type === 'register' && (
                        <div className="flex items-start gap-3 px-2 pt-2">
                             <div className="relative flex items-center">
                                <input 
                                    type="checkbox" 
                                    id="agree" 
                                    checked={agreed} 
                                    onChange={e => setAgreed(e.target.checked)}
                                    className="peer h-6 w-6 cursor-pointer appearance-none rounded-lg border-2 border-slate-200 bg-slate-50 transition-all checked:border-indigo-600 checked:bg-indigo-600 focus:outline-none focus:ring-4 focus:ring-indigo-100"
                                />
                                <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 transition-opacity peer-checked:opacity-100">
                                    <i className="fa-solid fa-check text-xs"></i>
                                </div>
                             </div>
                             <label htmlFor="agree" className="text-xs font-bold text-slate-500 cursor-pointer select-none leading-relaxed">
                                 I certify that I have read and agree to the <button type="button" onClick={() => setViewingLegal('terms')} className="text-indigo-600 hover:underline">Terms</button>, <button type="button" onClick={() => setViewingLegal('privacy')} className="text-indigo-600 hover:underline">Privacy Policy</button>, and <button type="button" onClick={() => setViewingLegal('consent')} className="text-indigo-600 hover:underline">Informed Consent</button>.
                             </label>
                        </div>
                    )}

                    <Button type="submit" disabled={loading} className="w-full py-4 md:py-6 text-lg md:text-xl">
                        {type === 'login' ? 'Log In' : 'Join Now'}
                    </Button>
                </form>

                {/* Login Disclaimer */}
                {type === 'login' && (
                    <p className="text-xs text-center text-slate-400 mt-6 font-medium leading-relaxed">
                        By continuing, you acknowledge that you have read and understood our <button type="button" onClick={() => setViewingLegal('terms')} className="text-indigo-500 font-bold hover:underline">Terms</button>, <button type="button" onClick={() => setViewingLegal('privacy')} className="text-indigo-500 font-bold hover:underline">Privacy Policy</button>, and <button type="button" onClick={() => setViewingLegal('consent')} className="text-indigo-500 font-bold hover:underline">Informed Consent</button>.
                    </p>
                )}

                <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col gap-4">
                    <button 
                        type="button"
                        onClick={() => setType(type === 'login' ? 'register' : 'login')} 
                        className="w-full text-slate-500 font-bold text-xs uppercase tracking-wider hover:text-indigo-600 transition-colors py-1 text-center"
                    >
                        {type === 'login' ? "Don't have an account? Create an Account" : "Already registered? Sign In to Portal"}
                    </button>

                    <div className="relative flex py-1 items-center">
                        <div className="flex-grow border-t border-slate-100"></div>
                        <span className="shrink-0 mx-3 text-[10px] font-black uppercase tracking-widest text-slate-300">Fast Track</span>
                        <div className="flex-grow border-t border-slate-100"></div>
                    </div>

                    <div className="space-y-1.5">
                        <button 
                            type="button"
                            onClick={async () => {
                                setLoading(true);
                                try {
                                    await loginGuest();
                                    showToast("Welcome Guest", "You are signed in as a guest. You can book consultations now and save your password anytime in your dashboard.", "success");
                                    setPage('doctors');
                                } catch (e: any) {
                                    showToast("Guest Access Error", e.message || "Failed to start guest session.", "error");
                                } finally {
                                    setLoading(false);
                                }
                            }} 
                            disabled={loading}
                            className="w-full py-4 px-4 bg-slate-50 hover:bg-indigo-50/60 hover:border-indigo-200 text-slate-700 hover:text-indigo-700 rounded-2xl font-bold text-xs transition-all border border-slate-200/90 flex items-center justify-center gap-2.5 shadow-2xs group cursor-pointer"
                        >
                            <i className="fa-solid fa-user-clock text-indigo-600 text-sm group-hover:scale-110 transition-transform"></i>
                            <span>Continue as Guest Patient</span>
                        </button>
                        <p className="text-[11px] text-center text-slate-400 font-medium">
                            Book appointments immediately. You can set up your permanent email & password anytime.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
